import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Zap, TrendingUp, Target, Shield, Crown, Play, Pause, ArrowRight, CheckCircle, Star, Trophy, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ProUpgradePromo = ({ isOpen, onClose, onUpgrade }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  
  const SLIDE_DURATION = 5000; // 5 seconds per slide
  
  const slides = [
    {
      id: 1,
      type: 'intro',
      title: 'Upgrade to PRO',
      subtitle: 'Take Your Trading to the Next Level',
      description: 'Unlock advanced features designed for serious traders',
      icon: Crown,
      gradient: 'from-yellow-500 to-orange-600',
      bgGradient: 'from-yellow-900/40 via-orange-900/40 to-red-900/40',
      features: [
        'Advanced Scalping Signals',
        'Multiple Timeframe Analysis',
        'PRO-Only Asset Pairs'
      ]
    },
    {
      id: 2,
      type: 'benefit',
      title: 'Boost Your Win Rate',
      subtitle: 'PRO Scalper Bot Advantages',
      description: 'Our PRO signals use advanced technical analysis for better accuracy',
      icon: TrendingUp,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-900/40 via-emerald-900/40 to-teal-900/40',
      stats: [
        { label: 'Signal Accuracy', value: '85%+', icon: Target },
        { label: 'Technical Indicators', value: '5+', icon: Zap },
        { label: 'Entry Timing', value: 'Precise', icon: Shield }
      ]
    },
    {
      id: 3,
      type: 'features',
      title: 'PRO Exclusive Features',
      subtitle: 'What You Get with PRO',
      icon: Star,
      gradient: 'from-purple-500 to-pink-600',
      bgGradient: 'from-purple-900/40 via-pink-900/40 to-rose-900/40',
      featureList: [
        { title: 'Binary Scalper PRO', desc: 'Advanced signal generation with real-time analysis' },
        { title: 'OTC & Forex Pairs', desc: 'Access to premium trading pairs' },
        { title: 'Financial Game Planner', desc: 'Track and optimize your trading finances' },
        { title: 'Priority Support', desc: 'Get help when you need it most' }
      ]
    },
    {
      id: 4,
      type: 'comparison',
      title: 'Standard vs PRO',
      subtitle: 'See the Difference',
      icon: Zap,
      gradient: 'from-blue-500 to-cyan-600',
      bgGradient: 'from-blue-900/40 via-cyan-900/40 to-teal-900/40',
      comparison: [
        { feature: 'Signal Types', standard: 'Basic', pro: 'Advanced Scalping' },
        { feature: 'Technical Analysis', standard: 'Limited', pro: 'Full (RSI, EMA, BB)' },
        { feature: 'Entry Timing', standard: 'Manual', pro: 'Auto-Calculated' },
        { feature: 'Financial Planner', standard: '❌', pro: '✅' }
      ]
    },
    {
      id: 5,
      type: 'howto',
      title: 'How to Upgrade',
      subtitle: '3 Simple Steps',
      icon: ArrowRight,
      gradient: 'from-indigo-500 to-violet-600',
      bgGradient: 'from-indigo-900/40 via-violet-900/40 to-purple-900/40',
      steps: [
        { num: 1, title: 'Click Upgrade', desc: 'Open your profile and select "Go Pro"' },
        { num: 2, title: 'Complete Payment', desc: 'Pay the one-time upgrade fee' },
        { num: 3, title: 'Start Trading', desc: 'Access PRO features instantly!' }
      ]
    },
    {
      id: 6,
      type: 'proof',
      title: 'Real PRO Results',
      subtitle: 'See What PRO Traders Are Achieving',
      icon: Trophy,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-900/40 via-emerald-900/40 to-teal-900/40',
      proofImage: 'https://customer-assets.emergentagent.com/job_tradewise-459/artifacts/7nbk5u2x_Screenshot%202025-12-12%20220616.png',
      caption: '+$40 Profit on a Single Trade!'
    },
    {
      id: 7,
      type: 'cta',
      title: 'Ready to Go PRO?',
      subtitle: 'Start Winning More Today',
      description: 'Join thousands of successful PRO traders',
      icon: Crown,
      gradient: 'from-yellow-500 to-orange-600',
      bgGradient: 'from-yellow-900/40 via-orange-900/40 to-red-900/40',
      cta: true
    }
  ];
  
  // Auto-advance slides
  useEffect(() => {
    if (!isOpen || !isPlaying) return;
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / (SLIDE_DURATION / 100));
      });
    }, 100);
    
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
      setProgress(0);
    }, SLIDE_DURATION);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(slideInterval);
    };
  }, [isOpen, isPlaying, slides.length]);
  
  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setProgress(0);
      setIsPlaying(true);
    }
  }, [isOpen]);
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };
  
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };
  
  if (!isOpen) return null;
  
  const slide = slides[currentSlide];
  const IconComponent = slide.icon;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white/70 hover:text-white transition-all"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-20">
          <div 
            className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Slide indicators */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentSlide 
                  ? 'bg-white w-6' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
        
        {/* Slide content */}
        <div className={`relative min-h-[480px] bg-gradient-to-br ${slide.bgGradient} transition-all duration-500`}>
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl animate-pulse delay-500" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          
          <div className="relative z-10 p-8 pt-16 flex flex-col items-center justify-center min-h-[480px]">
            {/* Icon */}
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center mb-6 shadow-lg animate-bounce-slow`}>
              <IconComponent className="w-10 h-10 text-white" />
            </div>
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-2 animate-slideUp">
              {slide.title}
            </h2>
            <p className={`text-lg bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent font-medium mb-4 animate-slideUp`}>
              {slide.subtitle}
            </p>
            
            {/* Content based on slide type */}
            {slide.type === 'intro' && (
              <div className="text-center animate-fadeIn">
                <p className="text-white/70 mb-6 max-w-md">{slide.description}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {slide.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-white text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {slide.type === 'benefit' && (
              <div className="animate-fadeIn">
                <p className="text-white/70 mb-6 text-center max-w-md">{slide.description}</p>
                <div className="grid grid-cols-3 gap-4">
                  {slide.stats.map((stat, idx) => (
                    <div key={idx} className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
                      <stat.icon className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-white/60">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {slide.type === 'features' && (
              <div className="grid grid-cols-2 gap-4 animate-fadeIn max-w-lg">
                {slide.featureList.map((feature, idx) => (
                  <div key={idx} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-xs text-white/60">{feature.desc}</p>
                  </div>
                ))}
              </div>
            )}
            
            {slide.type === 'comparison' && (
              <div className="w-full max-w-md animate-fadeIn">
                <div className="bg-white/10 rounded-xl overflow-hidden backdrop-blur-sm">
                  <div className="grid grid-cols-3 gap-0 text-center text-sm">
                    <div className="p-3 bg-white/5 font-medium text-white/60">Feature</div>
                    <div className="p-3 bg-white/5 font-medium text-white/60">Standard</div>
                    <div className="p-3 bg-yellow-500/20 font-medium text-yellow-300">PRO</div>
                  </div>
                  {slide.comparison.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-0 text-center text-sm border-t border-white/10">
                      <div className="p-3 text-white/80">{row.feature}</div>
                      <div className="p-3 text-white/50">{row.standard}</div>
                      <div className="p-3 text-green-400 font-medium">{row.pro}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {slide.type === 'howto' && (
              <div className="flex flex-col md:flex-row gap-6 animate-fadeIn">
                {slide.steps.map((step, idx) => (
                  <div key={idx} className="flex-1 text-center">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${slide.gradient} flex items-center justify-center mx-auto mb-3 text-xl font-bold text-white`}>
                      {step.num}
                    </div>
                    <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                    <p className="text-xs text-white/60">{step.desc}</p>
                    {idx < slide.steps.length - 1 && (
                      <ArrowRight className="hidden md:block w-6 h-6 text-white/30 absolute right-0 top-1/2 -translate-y-1/2" />
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {slide.type === 'proof' && (
              <div className="text-center animate-fadeIn">
                <div className="relative mx-auto max-w-md mb-4">
                  <div className="absolute -inset-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur-lg opacity-30 animate-pulse"></div>
                  <div className="relative rounded-xl overflow-hidden border-2 border-green-500/50 shadow-2xl">
                    <img 
                      src={slide.proofImage} 
                      alt="PRO Trading Result" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <DollarSign className="w-6 h-6 text-green-400" />
                  <span className="text-2xl font-bold text-green-400">{slide.caption}</span>
                </div>
                <p className="text-white/60 text-sm">Real results from PRO Scalper Bot</p>
              </div>
            )}
            
            {slide.type === 'cta' && (
              <div className="text-center animate-fadeIn">
                <p className="text-white/70 mb-8 max-w-md">{slide.description}</p>
                <Button
                  onClick={onUpgrade}
                  className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-yellow-500/25 transition-all transform hover:scale-105"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  Upgrade to PRO Now
                </Button>
                <p className="text-white/40 text-xs mt-4">One-time payment • Instant access</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation controls */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-20">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={togglePlay}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          
          <button
            onClick={nextSlide}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        {/* Skip button */}
        <button
          onClick={onClose}
          className="absolute bottom-4 right-16 text-white/40 hover:text-white/70 text-sm transition-all"
        >
          Skip
        </button>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        .animate-slideUp { animation: slideUp 0.5s ease-out; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default ProUpgradePromo;
